import { useAuth0 } from "ct-auth"
import CloseIcon from "@mui/icons-material/Close"
import { Box, Drawer, IconButton, Typography } from "@mui/material"
import DOMPurify from "dompurify"
import MarkdownIt from "markdown-it"
import { useEffect, useState } from "react"
import { useLocation } from "react-router-dom"
import { isManager, isSponsor } from "../../../data-utils/authUtils"
import { helpVideoUrl, resolveHelp, routeKeyForPath } from "../../../data-utils/helpContent"
import { HELP_VIDEO_BASE } from "../../../utilities/constants"

// Render help bodies as sanitized HTML (no raw HTML in source; linkify + line breaks).
const md = new MarkdownIt({ html: false, linkify: true, breaks: true })

const roleForUser = (user) => (isManager(user) ? "manager" : isSponsor(user) ? "sponsor" : "player")

/**
 * In-product per-page help DRAWER, scoped to the current route + the user's role
 * (see data-utils/helpContent.js).
 *
 * It has NO floating button — so help never hovers over page content or action buttons.
 * It opens on demand via the `window` event "ct:open-help", which is dispatched by the
 * inline `<HelpTip/>` "?" affordances next to page titles and by the account menu's "Help"
 * item. Mounted once globally (see the user-state provider).
 */
export default function HelpCenter() {
  const [open, setOpen] = useState(false)
  // Optional routeKey override carried on the open event — lets same-URL surfaces (e.g. solo vs.
  // collaborative gameplay) request a specific help entry instead of the pathname-derived one.
  const [routeKeyOverride, setRouteKeyOverride] = useState(null)
  const { pathname } = useLocation()
  const { user } = useAuth0()

  useEffect(() => {
    const onOpen = (e) => {
      setRouteKeyOverride(e?.detail?.routeKey ?? null)
      setOpen(true)
    }
    window.addEventListener("ct:open-help", onOpen)
    return () => window.removeEventListener("ct:open-help", onOpen)
  }, [])

  const routeKey = routeKeyOverride ?? routeKeyForPath(pathname)
  const entry = routeKey ? resolveHelp(routeKey, roleForUser(user)) : null
  const html = entry ? DOMPurify.sanitize(md.render(entry.bodyMarkdown || "")) : ""
  const videoUrl = helpVideoUrl({ routeKey, entry, base: HELP_VIDEO_BASE })

  const handleClose = () => {
    setOpen(false)
    setRouteKeyOverride(null)
  }

  return (
    <Drawer anchor="right" open={open} onClose={handleClose}>
      <Box sx={{ width: 380, maxWidth: "90vw", p: 2 }} role="complementary" aria-label="Page help">
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="h6">{entry ? entry.title : "Help"}</Typography>
          <IconButton aria-label="Close help" onClick={handleClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        {!entry && (
          <Typography variant="body2" color="text.secondary">
            Help for this page is coming soon.
          </Typography>
        )}
        {videoUrl && (
          <Box sx={{ mb: 2 }}>
            <video src={videoUrl} controls style={{ width: "100%", borderRadius: 8 }} />
          </Box>
        )}
        {entry && (
          <Box
            className="ct-help-body"
            sx={{ "& p": { mt: 0 }, "& ul": { pl: 2.5, my: 1 }, "& li": { mb: 0.5 } }}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        )}
      </Box>
    </Drawer>
  )
}
